import { useEffect, useState, useCallback } from 'react'
import { Landmark, Plus, X, ArrowDownCircle, ArrowUpCircle, Lock, Settings } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dth = (s) => (s ? new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const TIPO_LABEL = { entrada: 'Entrada', saida: 'Saída', reserva: 'Reserva', liberacao: 'Liberação' }
const TIPO_COR = { entrada: 'text-emerald-600', saida: 'text-red-600', reserva: 'text-amber-600', liberacao: 'text-blue-600' }

export default function Fundo() {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const [movOpen, setMovOpen] = useState(false)
  const [cfgOpen, setCfgOpen] = useState(false)

  const carregar = useCallback(() => {
    setLoading(true)
    api.get('/fundo').then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false))
  }, [])
  useEffect(() => { carregar() }, [carregar])

  if (loading) return <div className="flex justify-center py-16"><Loader text="Carregando fundo..." /></div>
  if (!d) return <p className="text-gray-400 p-8">Não foi possível carregar o Fundo de Incentivo.</p>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow"><Landmark size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Fundo de Incentivo</h1>
            <p className="text-sm text-gray-500">Recursos para campanhas, viagens, carros e premiações da rede.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCfgOpen(true)} className="flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-xl text-sm font-semibold text-gray-600"><Settings size={16} /> Origem dos recursos</button>
          <button onClick={() => setMovOpen(true)} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16} /> Movimento</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Saldo disponível" value={money(d.saldo)} color="from-emerald-400 to-teal-500" big />
        <Card label="Acumulado (entradas)" value={money(d.acumulado)} color="from-blue-400 to-cyan-500" />
        <Card label="Utilizado (saídas)" value={money(d.utilizado)} color="from-rose-400 to-red-500" />
        <Card label="Reservado (campanhas)" value={money(d.reservado)} color="from-amber-400 to-orange-500" />
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-sm text-gray-600">
        <span className="font-semibold text-gray-800">Origem configurada:</span>{' '}
        {d.config.pct_lucro_liquido}% do lucro líquido · {d.config.pct_faturamento}% do faturamento · fixo {money(d.config.valor_fixo_mensal)}/mês · patrocínios {d.config.aceita_patrocinios ? 'sim' : 'não'}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Data</th><th className="px-4 py-2">Tipo</th><th className="px-4 py-2">Origem</th><th className="px-4 py-2">Descrição</th><th className="px-4 py-2 text-right">Valor</th><th className="px-4 py-2">Responsável</th></tr></thead>
          <tbody>
            {(d.historico || []).map((m) => (
              <tr key={m.id} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-400 text-xs">{dth(m.created_at)}</td>
                <td className={`px-4 py-2 font-semibold ${TIPO_COR[m.tipo]}`}>{TIPO_LABEL[m.tipo] || m.tipo}</td>
                <td className="px-4 py-2 text-gray-500">{m.origem || '—'}</td>
                <td className="px-4 py-2 text-gray-700">{m.descricao || '—'}</td>
                <td className={`px-4 py-2 text-right font-bold ${TIPO_COR[m.tipo]}`}>{m.tipo === 'saida' ? '−' : ''}{money(m.valor)}</td>
                <td className="px-4 py-2 text-gray-400 text-xs">{m.responsavel || '—'}</td>
              </tr>
            ))}
            {(d.historico || []).length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Sem movimentações. Faça um aporte para iniciar o fundo.</td></tr>}
          </tbody>
        </table>
      </div>

      {movOpen && <MovForm onClose={() => setMovOpen(false)} onSalvo={() => { setMovOpen(false); carregar() }} />}
      {cfgOpen && <CfgForm cfg={d.config} onClose={() => setCfgOpen(false)} onSalvo={() => { setCfgOpen(false); carregar() }} />}
    </div>
  )
}

function Card({ label, value, color, big }) {
  return (
    <div className={`rounded-2xl p-4 shadow-sm border border-gray-100 ${big ? `bg-gradient-to-br ${color} text-white` : 'bg-white'}`}>
      <p className={`text-xl font-heading font-bold ${big ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      <p className={`text-xs mt-0.5 ${big ? 'text-white/80' : 'text-gray-500'}`}>{label}</p>
    </div>
  )
}

function MovForm({ onClose, onSalvo }) {
  const [f, setF] = useState({ tipo: 'entrada', valor: '', origem: 'aporte', descricao: '' })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const salvar = () => {
    if (!Number(f.valor)) return errorModal('Informe o valor.')
    setSaving(true)
    api.post('/fundo/movimento', { ...f, valor: Number(f.valor) })
      .then(() => { successModal('Movimento registrado.'); onSalvo() })
      .catch((e) => errorModal(e?.response?.data?.detail || 'Erro.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">Movimento do Fundo</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <select value={f.tipo} onChange={set('tipo')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm">
          <option value="entrada">Entrada (aporte/percentual)</option><option value="saida">Saída (premiação paga)</option>
          <option value="reserva">Reserva (orçamento de campanha)</option><option value="liberacao">Liberação de reserva</option>
        </select>
        <input type="number" value={f.valor} onChange={set('valor')} placeholder="Valor R$" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <select value={f.origem} onChange={set('origem')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm">
          <option value="aporte">Aporte manual</option><option value="lucro">% Lucro líquido</option><option value="faturamento">% Faturamento</option>
          <option value="fixo">Valor fixo</option><option value="patrocinio">Patrocínio</option><option value="campanha">Campanha</option>
        </select>
        <input value={f.descricao} onChange={set('descricao')} placeholder="Descrição" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Registrar'}</button>
      </div>
    </div>
  )
}

function CfgForm({ cfg, onClose, onSalvo }) {
  const [f, setF] = useState({ ...cfg })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
  const salvar = () => {
    setSaving(true)
    api.post('/fundo/config', {
      pct_lucro_liquido: Number(f.pct_lucro_liquido) || 0, pct_faturamento: Number(f.pct_faturamento) || 0,
      valor_fixo_mensal: Number(f.valor_fixo_mensal) || 0, aceita_patrocinios: !!f.aceita_patrocinios,
    }).then(() => { successModal('Configuração salva.'); onSalvo() }).catch(() => errorModal('Erro.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">Origem dos Recursos</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <label className="text-xs text-gray-500">% do lucro líquido mensal<input type="number" value={f.pct_lucro_liquido} onChange={set('pct_lucro_liquido')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1" /></label>
        <label className="text-xs text-gray-500">% do faturamento<input type="number" value={f.pct_faturamento} onChange={set('pct_faturamento')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1" /></label>
        <label className="text-xs text-gray-500">Valor fixo mensal (R$)<input type="number" value={f.valor_fixo_mensal} onChange={set('valor_fixo_mensal')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1" /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.aceita_patrocinios} onChange={set('aceita_patrocinios')} /> Aceita patrocínios de parceiros</label>
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </div>
  )
}
